import type { RefObject } from 'react';
import { Box } from '@mui/material';
import {
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
  ReportProblem as CriticalIcon,
  Flag as BlockingIcon,
  GridView as AllIcon,
  Settings as MappingIcon,
  AutoFixHigh as QuickFillIcon,
  Cable as ConnectorsIcon,
  Tune as PropertiesIcon,
  Category as ComponentsIcon,
  StarBorder as RequiredIcon,
} from '@mui/icons-material';
import { WorkspaceFilterSection, WorkspaceFilterItem } from '../../../shared/ui/workflow/WorkspaceFilterRail';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import type { ValidationIssueFamilyCategory } from '../lib/validationIssueFamilies';
import type { ValidationBlockerFilter, ValidationSeverityFilter } from '../hooks/useValidationWorkspace';

interface ValidationRailFilterSectionsProps {
  severityFilter: ValidationSeverityFilter;
  onSelectSeverity: (filter: ValidationSeverityFilter) => void;
  blockerFilter: ValidationBlockerFilter;
  onSelectBlockerFilter: (filter: ValidationBlockerFilter) => void;
  requiredOnly: boolean;
  onToggleRequiredOnly: () => void;
  categoryFilter: ValidationIssueFamilyCategory | 'all';
  onSelectCategory: (filter: ValidationIssueFamilyCategory | 'all') => void;
  severityCounts: Record<ValidationSeverityFilter, number>;
  categoryCounts: Record<ValidationIssueFamilyCategory, number>;
  blockerCount: number;
  requiredOnlyCount: number;
  showPassedOnly: boolean;
  onShowPassedOnly: (value: boolean) => void;
  filtersRef?: RefObject<HTMLDivElement | null>;
  repairAreaRef?: RefObject<HTMLDivElement | null>;
}

export default function ValidationRailFilterSections({
  severityFilter,
  onSelectSeverity,
  blockerFilter,
  onSelectBlockerFilter,
  requiredOnly,
  onToggleRequiredOnly,
  categoryFilter,
  onSelectCategory,
  severityCounts,
  categoryCounts,
  blockerCount,
  requiredOnlyCount,
  showPassedOnly,
  onShowPassedOnly,
  filtersRef,
  repairAreaRef,
}: ValidationRailFilterSectionsProps) {
  const selectSeverity = (filter: ValidationSeverityFilter) => {
    onShowPassedOnly(false);
    onSelectSeverity(filter);
  };

  const toggleBlockerFilter = () => {
    onShowPassedOnly(false);
    onSelectBlockerFilter(blockerFilter === 'blocking' ? 'all' : 'blocking');
  };

  const toggleRequiredOnly = () => {
    onShowPassedOnly(false);
    onToggleRequiredOnly();
  };

  const selectCategory = (filter: ValidationIssueFamilyCategory | 'all') => {
    onShowPassedOnly(false);
    onSelectCategory(categoryFilter === filter ? 'all' : filter);
  };

  const isAllSelected =
    !showPassedOnly && severityFilter === 'all' && blockerFilter !== 'blocking' && !requiredOnly;

  return (
    <>
      <Box ref={filtersRef} data-testid="validation-tour-target-rail-filters">
        <WorkspaceFilterSection
          title="Quick filters"
          description="Severity"
          descriptionBold
          highlighted={severityFilter !== 'all' || blockerFilter === 'blocking' || requiredOnly}
        >
          <WorkspaceFilterItem
            icon={<AllIcon sx={{ fontSize: 16 }} />}
            label="All issues"
            count={severityCounts.all}
            selected={isAllSelected}
            help={validationActionTooltips.filterAllIssues}
            onClick={() => {
              onShowPassedOnly(false);
              onSelectSeverity('all');
              onSelectBlockerFilter('all');
              if (requiredOnly) {
                onToggleRequiredOnly();
              }
            }}
          />
          <WorkspaceFilterItem
            icon={<CriticalIcon sx={{ fontSize: 16 }} />}
            label="Critical"
            count={severityCounts.critical}
            selected={!showPassedOnly && severityFilter === 'critical'}
            help={validationActionTooltips.filterCriticalIssues}
            onClick={() => selectSeverity('critical')}
            tone="error"
          />
          <WorkspaceFilterItem
            icon={<ErrorIcon sx={{ fontSize: 16 }} />}
            label="Errors"
            count={severityCounts.error}
            selected={!showPassedOnly && severityFilter === 'error'}
            help={validationActionTooltips.filterErrorIssues}
            onClick={() => selectSeverity('error')}
            tone="error"
          />
          <WorkspaceFilterItem
            icon={<WarningIcon sx={{ fontSize: 16 }} />}
            label="Warnings"
            count={severityCounts.warning}
            selected={!showPassedOnly && severityFilter === 'warning'}
            help={validationActionTooltips.filterWarningIssues}
            onClick={() => selectSeverity('warning')}
            tone="warning"
          />
        </WorkspaceFilterSection>

        <WorkspaceFilterSection
          title="Focus"
          description={blockerCount > 0 ? `${blockerCount} blocking issues` : 'Narrow your view'}
          highlighted={blockerFilter === 'blocking' || requiredOnly}
        >
          <WorkspaceFilterItem
            icon={<BlockingIcon sx={{ fontSize: 16 }} />}
            label="Blockers only"
            count={blockerCount}
            selected={!showPassedOnly && blockerFilter === 'blocking'}
            help={validationActionTooltips.filterBlockers}
            onClick={toggleBlockerFilter}
            tone="error"
          />
          <WorkspaceFilterItem
            icon={<RequiredIcon sx={{ fontSize: 16 }} />}
            label="Required fields"
            count={requiredOnlyCount}
            selected={!showPassedOnly && requiredOnly}
            help={validationActionTooltips.filterRequiredFields}
            onClick={toggleRequiredOnly}
            tone="warning"
          />
        </WorkspaceFilterSection>
      </Box>

      <Box ref={repairAreaRef} data-testid="validation-tour-target-rail-repair-area">
        <WorkspaceFilterSection
          title="Repair category"
          description="Filter by category"
          highlighted={categoryFilter !== 'all'}
        >
          <WorkspaceFilterItem
            icon={<MappingIcon sx={{ fontSize: 16 }} />}
            label="Mapping"
            count={categoryCounts.mapping}
            selected={!showPassedOnly && categoryFilter === 'mapping'}
            help={validationActionTooltips.filterMappingIssues}
            onClick={() => selectCategory('mapping')}
          />
          <WorkspaceFilterItem
            icon={<QuickFillIcon sx={{ fontSize: 16 }} />}
            label="Export data"
            count={categoryCounts['required-export']}
            selected={!showPassedOnly && categoryFilter === 'required-export'}
            help={validationActionTooltips.filterExportDataIssues}
            onClick={() => selectCategory('required-export')}
          />
          <WorkspaceFilterItem
            icon={<ConnectorsIcon sx={{ fontSize: 16 }} />}
            label="Connectors"
            count={categoryCounts.connectors}
            selected={!showPassedOnly && categoryFilter === 'connectors'}
            help={validationActionTooltips.filterConnectorIssues}
            onClick={() => selectCategory('connectors')}
          />
          <WorkspaceFilterItem
            icon={<PropertiesIcon sx={{ fontSize: 16 }} />}
            label="Properties"
            count={categoryCounts.properties}
            selected={!showPassedOnly && categoryFilter === 'properties'}
            help={validationActionTooltips.filterPropertyIssues}
            onClick={() => selectCategory('properties')}
          />
          <WorkspaceFilterItem
            icon={<ComponentsIcon sx={{ fontSize: 16 }} />}
            label="Components"
            count={categoryCounts.components}
            selected={!showPassedOnly && categoryFilter === 'components'}
            help={validationActionTooltips.filterComponentIssues}
            onClick={() => selectCategory('components')}
          />
        </WorkspaceFilterSection>
      </Box>
    </>
  );
}
